'use client'

import { memo, useCallback } from 'react'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { CalendarIcon, Loader2Icon, CheckCircle2Icon, PlayIcon } from 'lucide-react'
import {
  IconCircleDashedCheck,
  IconChevronDown,
  IconChevronRight,
  IconPlayerPlay
} from '@tabler/icons-react'
import { Button } from '@/components/ui/button'
import { Streamdown, type Components } from 'streamdown'
import { ExtendedTask } from './task-types'
import { styles, getStatusIcon, formatStatus, formatActionName, formatDateTime } from './task-utils'
import { convertPatternToNaturalLanguage } from './task-pattern'
import { toast } from '@/components/ui/use-toast'
import { authenticatedFetch } from '@/lib/auth/authenticated-fetch'
import { Database } from '@/supabase/types'
import { useTranslation } from 'react-i18next'
import { useSplitPaneNavigation } from '@/components/splitview/split-pane-context'
import { streamdownStaticProps } from '@/components/messages/streamdown-animation'

interface TaskCardProps {
  task: ExtendedTask
  isRunning: boolean
  runSuccess: boolean
  onRunNow: (taskId: string) => void
  expandedKeys: Record<string, boolean>
  onToggleExpand: (key: string) => void
  formatValue: (key: string, value: any) => JSX.Element
  formatActionParams: (params: Record<string, any>) => JSX.Element
  markdownComponents: Components
}

export const TaskCard = memo(
  ({
    task,
    isRunning,
    runSuccess,
    onRunNow,
    expandedKeys,
    onToggleExpand,
    formatValue,
    formatActionParams,
    markdownComponents
  }: TaskCardProps) => {
    const { navigateToHref } = useSplitPaneNavigation()
    const { t } = useTranslation()

    const handleRunNow = useCallback(() => {
      onRunNow(task.id)
    }, [onRunNow, task.id])

    const handleRunInChat = async () => {
      try {
        const response = await authenticatedFetch('/api/chat/task', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            taskId: task.id,
            workspaceId: task.workspace_id
          })
        })

        if (!response.ok) {
          throw new Error(t('Failed to create task chat'))
        }

        const { chatId } = await response.json()
        navigateToHref(`/chat/${chatId}`)
      } catch (error) {
        console.error(error)
        toast({
          title: t('Error'),
          description: t('Failed to run task'),
          variant: 'destructive'
        })
      }
    }

    const renderPdfContent = (action: any) => {
      if (action.function === 'generate_pdf') {
        const pdfData = action.result?.pdf
        const content =
          action.result?.content?.content || action.result?.content || action.params?.content

        return (
          <div className="mt-4 space-y-4">
            {content && (
              <div className="prose prose-sm max-w-none dark:prose-invert">
                <Streamdown {...streamdownStaticProps}>
                  {content}
                </Streamdown>
              </div>
            )}

            {pdfData && (
              <div>
                <div className="mb-2 font-medium">{t('Generated PDF')}:</div>
                <object
                  data={pdfData}
                  type="application/pdf"
                  className="h-[600px] w-full rounded border"
                >
                  <div className="rounded-lg border border-border/70 bg-muted/30 p-4">
                    <p>{t('PDF preview not available. Click below to download')}:</p>
                    <a
                      href={pdfData}
                      download="generated-report.pdf"
                      className="text-primary hover:text-primary/80"
                    >
                      {t('Download PDF')}
                    </a>
                  </div>
                </object>
              </div>
            )}
          </div>
        )
      }
      return null
    }

    const renderActions = useCallback(() => {
      if (!task.action) return null

      const actions = Array.isArray(task.action) ? task.action : [task.action]

      return (
        <div className="space-y-2">
          <p className="text-sm font-medium">{t('Actions')}:</p>
          <div className="flex flex-col gap-2">
            {actions.map((action, index) => (
              <div
                key={action.id || index}
                className="rounded-lg border border-border/70 bg-muted/30 p-2"
              >
                <div className="flex items-center gap-2">
                  <IconCircleDashedCheck className="size-4 opacity-70" />
                  <span className="font-medium">{formatActionName(action.function, t)}</span>
                  {action.status && (
                    <Badge variant="outline" className="ml-2">
                      {action.status}
                    </Badge>
                  )}
                </div>
                {action.params && Object.keys(action.params).length > 0 && (
                  <div className="ml-6 mt-1 max-w-full overflow-hidden">
                    {formatActionParams(action.params)}
                  </div>
                )}
                {action.error && (
                  <div className="ml-6 mt-1 text-sm text-destructive">
                    {t('Error')}: {action.error}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )
    }, [task.action, formatActionParams, t])

    const renderSchedule = useCallback(() => {
      if (!task.schedule) return null

      return (
        <div className="space-y-2">
          <p className="text-sm font-medium">{t('Schedule')}:</p>
          <div className="text-sm text-muted-foreground">
            {task.schedule.type === 'once'
              ? (() => {
                  try {
                    const date = new Date(task.schedule.datetime!)
                    if (isNaN(date.getTime())) {
                      return t('Invalid schedule time')
                    }
                    return `${t('Once at')} ${formatDateTime(date, t)}`
                  } catch (e) {
                    console.error('Error parsing date:', task.schedule.datetime, e)
                    return t('Invalid schedule time')
                  }
                })()
              : task.schedule.pattern
                ? convertPatternToNaturalLanguage(task.schedule.pattern, t)
                : t('Recurring')}
          </div>
        </div>
      )
    }, [task.schedule, t])

    return (
      <Card className="border-border/70 bg-card p-4">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              {getStatusIcon(task.status)}
              <Badge variant="outline" className="capitalize">
                {formatStatus(task.status, t)}
              </Badge>
              <span className="text-sm font-medium">{task.name}</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {task.progress !== undefined && (
                <div className="flex items-center gap-2">
                  <div className={styles.progressBar.base}>
                    <div
                      className={styles.progressBar.fill}
                      style={{ width: `${task.progress}%` }}
                    />
                  </div>
                  <span className="text-sm text-muted-foreground">{task.progress}%</span>
                </div>
              )}
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleRunNow}
                  disabled={isRunning || task.status === 'in_progress'}
                  className="relative shrink-0"
                >
                  <div className="relative size-4">
                    {isRunning && (
                      <Loader2Icon className="absolute inset-0 size-4 text-primary motion-safe:animate-spin motion-safe:transition-opacity motion-safe:duration-300 motion-safe:ease-in-out motion-reduce:animate-none" />
                    )}
                    {runSuccess && (
                      <CheckCircle2Icon className="absolute inset-0 size-4 text-emerald-500 motion-safe:transition-opacity motion-safe:duration-300 motion-safe:ease-in-out" />
                    )}
                    {!isRunning && !runSuccess && (
                      <PlayIcon className="absolute inset-0 size-4 motion-safe:transition-transform motion-safe:duration-300 motion-safe:ease-in-out hover:motion-safe:scale-110" />
                    )}
                  </div>
                  <span className="sr-only">Run Now</span>
                </Button>
                {/* <Button
                variant="ghost"
                size="sm"
                onClick={handleRunInChat}
                disabled={isRunning}
                className="gap-1.5 text-xs"
              >
                Open in Chat
              </Button> */}
              </div>
            </div>
          </div>

          {task.description && (
            <div className="text-sm text-muted-foreground">
              <Streamdown
                {...streamdownStaticProps}
                className="prose prose-sm max-w-none break-words dark:prose-invert"
                components={markdownComponents}
              >
                {task.description}
              </Streamdown>
            </div>
          )}

          {task.due_date && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CalendarIcon size={16} />
              <span>
                {t('Due')}: {new Date(task.due_date).toLocaleString()}
              </span>
            </div>
          )}

          {renderActions()}
          {renderSchedule()}
        </div>
      </Card>
    )
  },
  (prevProps, nextProps) => {
    // Custom comparison function for memo
    return (
      prevProps.task.id === nextProps.task.id &&
      prevProps.task.status === nextProps.task.status &&
      prevProps.task.progress === nextProps.task.progress &&
      prevProps.isRunning === nextProps.isRunning &&
      prevProps.runSuccess === nextProps.runSuccess &&
      prevProps.expandedKeys === nextProps.expandedKeys
    )
  }
)

TaskCard.displayName = 'TaskCard'
